/**
 * TasksIndex — id + time-range lookups over a source row's
 * task cells.
 *
 * Built lazily: consumers obtain it through `row.as(TASKS_FACADE)`
 * (see `facades/tasks-facade.ts`). On first access the facade
 * walks the row's current cells, runs each through
 * `TASK_SHAPE.probe`, keeps what matches, and populates the
 * index. Subsequent `ColumnDataChanged(rowId)` events mark the
 * index dirty; the next read rebuilds.
 *
 * Mutations never land here — they go through row.write via
 * the facade, and the rebuild catches them on the next read.
 */

import type { TaskId } from "../ids/ids.ts";
import type { Option } from "../core/option.ts";
import type { DateTime, Utc } from "@tento-chrono";

import { defineShape, type Shape } from "../core/shape.ts";
import type { CellValue, TaskCellValue } from "../core/dtype.ts";
import type { Json } from "../core/json.ts";
import { taskCalendarBelongs, TaskCalendarItem } from "./tasks-index-types.ts";

export { TaskCalendarItem };

/**
 * Shape probe for `TaskCellValue` — the structural contract an index
 * consumer requires. A FormulaDataSource that synthesizes
 * `{ id, title, status?, scheduledStart?, … }` cells will
 * match without any per-row declaration.
 */
export const TASK_SHAPE: Shape<TaskCellValue> = defineShape<TaskCellValue>(
  "task/v1",
  (value): value is TaskCellValue => {
    if (!value || typeof value !== "object") return false;
    return "id" in value && typeof value.id === "string" &&
      "title" in value && typeof value.title === "string";
  },
);

/** The tasks index's counters from its last populate: cells probed and tasks indexed. */
export interface TasksIndexDiagnostics {
  /** Total cells fed into `populate` most recently. */
  probed: number;
  /** Cells that passed `TASK_SHAPE.probe` and were indexed. */
  accepted: number;
  /** Cells that failed the probe and were dropped. */
  dropped: number;
}

/** One calendar item in the index's start-sorted range array (`ordinal`: its insertion order). */
type IndexedTaskRange = {
  readonly id: TaskId;
  readonly item: TaskCalendarItem;
  readonly range: { readonly start: number; readonly end: number };
  readonly ordinal: number;
};

const EMPTY_INDEXED_TASK_RANGES: readonly IndexedTaskRange[] = Object.freeze([]);

/**
 * The index behind `TasksFacade`: a row's tasks by id (`peekAll`), with
 * calendar items for the tasks that have a placement (`peekInRange`, a
 * max-end segment tree over start-sorted ranges).
 */
export class TasksIndex {
  readonly #byId = new Map<TaskId, TaskCellValue>();
  readonly #calendarItems = new Map<TaskId, TaskCalendarItem>();
  #ranges: readonly IndexedTaskRange[] = EMPTY_INDEXED_TASK_RANGES;
  #maxEnds: Option<Float64Array> = null;
  #leafBase = 0;
  #diagnosticCounts: TasksIndexDiagnostics = {
    probed: 0,
    accepted: 0,
    dropped: 0,
  };

  /**
   * Rebuild the index from a canonical cell stream. Caller is
   * responsible for decoding cells through the shape's
   * `probe` filter before handing the values in — this method
   * trusts its input. Values are stored by `id`; duplicate ids
   * upsert (last write wins).
   */
  populate(tasks: readonly TaskCellValue[]): void {
    this.#byId.clear();
    this.#calendarItems.clear();
    let accepted = 0;
    for (const task of tasks) {
      this.#byId.set(task.id, task);
      if (task.scheduledStart || task.dueDate) {
        this.#calendarItems.set(task.id, new TaskCalendarItem(task));
      } else {
        this.#calendarItems.delete(task.id);
      }
      accepted++;
    }
    this.#rebuildRanges();
    this.#diagnosticCounts = {
      probed: tasks.length,
      accepted,
      dropped: 0,
    };
  }

  /**
   * Populate from raw cell values that may or may not be task-shaped.
   * Drops values that fail the probe, updating diagnostics counters.
   * Convenience for facades that receive unshaped cell streams.
   */
  populateFromRaw(values: readonly (Json | CellValue)[]): void {
    this.#byId.clear();
    this.#calendarItems.clear();
    let accepted = 0;
    let dropped = 0;
    for (const v of values) {
      if (!TASK_SHAPE.probe(v)) {
        dropped++;
        continue;
      }
      this.#byId.set(v.id, v);
      if (v.scheduledStart || v.dueDate) {
        this.#calendarItems.set(v.id, new TaskCalendarItem(v));
      } else {
        this.#calendarItems.delete(v.id);
      }
      accepted++;
    }
    this.#rebuildRanges();
    this.#diagnosticCounts = { probed: values.length, accepted, dropped };
  }

  // ── Queries ────────────────────────────────────────────────────────

  peek(id: TaskId): Option<TaskCellValue> {
    return this.#byId.get(id) ?? null;
  }

  peekAll(): readonly TaskCellValue[] {
    return [...this.#byId.values()];
  }

  /**
   * Every task whose computed time range overlaps `range`
   * (half-open). Tasks without a scheduled time or due date are
   * excluded (they have no calendar placement).
   */
  peekInRange(range: DateTime.Range<Utc>): readonly TaskCalendarItem[] {
    const maxEnds = this.#maxEnds;
    if (maxEnds == null || this.#ranges.length === 0) return [];
    const found: IndexedTaskRange[] = [];
    const stack = [{ node: 1, lo: 0, hi: this.#leafBase }];
    while (stack.length > 0) {
      const segment = stack.pop();
      if (segment == null) continue;
      const first = this.#ranges[segment.lo];
      const maxEnd = maxEnds[segment.node];
      // Starts are sorted; the entire subtree has this maximum end.
      if (first == null || maxEnd == null || first.range.start >= range.end.mse || maxEnd <= range.start.mse) continue;
      if (segment.hi - segment.lo === 1) {
        if (taskCalendarBelongs(first.item.time, range)) found.push(first);
        continue;
      }
      const mid = Math.floor((segment.lo + segment.hi) / 2);
      stack.push({ node: segment.node * 2 + 1, lo: mid, hi: segment.hi });
      stack.push({ node: segment.node * 2, lo: segment.lo, hi: mid });
    }
    // Keep the existing public Map insertion order, not index traversal order.
    return found.sort((a, b) => a.ordinal - b.ordinal).map(record => record.item);
  }

  #rebuildRanges(): void {
    const ranges = [...this.#calendarItems.values()]
      .map((item, ordinal) => ({ id: item.id, item, range: { start: item.time.start.mse, end: item.time.end.mse }, ordinal }))
      .sort((a, b) => a.range.start - b.range.start || a.ordinal - b.ordinal);
    this.#ranges = ranges;
    if (ranges.length === 0) {
      this.#maxEnds = null;
      this.#leafBase = 0;
      return;
    }
    let base = 1;
    while (base < ranges.length) base *= 2;
    const maxEnds = new Float64Array(base * 2);
    maxEnds.fill(Number.NEGATIVE_INFINITY);
    for (let i = 0; i < ranges.length; i++) {
      const record = ranges[i];
      if (record != null) maxEnds[base + i] = record.range.end;
    }
    for (let node = base - 1; node > 0; node--) {
      maxEnds[node] = Math.max(maxEnds[node * 2] ?? Number.NEGATIVE_INFINITY, maxEnds[node * 2 + 1] ?? Number.NEGATIVE_INFINITY);
    }
    this.#maxEnds = maxEnds;
    this.#leafBase = base;
  }

  get size(): number {
    return this.#byId.size;
  }

  diagnostics(): TasksIndexDiagnostics {
    return { ...this.#diagnosticCounts };
  }
}
